import { type UserData } from '@beat-venues/common-types';
import {
    QueryObserverResult,
    RefetchOptions,
    useQuery,
} from '@tanstack/react-query';
import { createContext, ReactNode } from 'react';

import { getUserData } from '../services';
import { getCookie, validateToken } from '../utils';

interface UserProviderProps {
    children: ReactNode;
}

interface UserContextType {
    user: UserData | undefined;
    getUser: (
        options?: RefetchOptions | undefined
    ) => Promise<QueryObserverResult<UserData, Error>>;
    isPending?: boolean;
}

const UserContext = createContext<UserContextType>({
    user: undefined,
    getUser: () => Promise.resolve({} as QueryObserverResult<UserData, Error>),
});

const UserProvider = ({ children }: UserProviderProps) => {
    const {
        data: user,
        refetch: getUser,
        isPending,
    } = useQuery({
        queryKey: ['user'],
        queryFn: async () => {
            const token = getCookie('token');
            await validateToken(token);
            let user;
            if (token) {
                user = await getUserData(token as string);
            }
            if (!user) {
                throw new Error('No user found');
            }

            return user;
        },
    });

    return (
        <UserContext.Provider value={{ user, getUser, isPending }}>
            {children}
        </UserContext.Provider>
    );
};

export { type UserContextType, UserContext, UserProvider };
